import type { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';

import { getAppSettings } from '~/lib/appSettings';

const addAxiosContext = (event: Sentry.Event, error: AxiosError): void => {
  // eslint-disable-next-line no-param-reassign
  event.contexts = {
    ...event.contexts,
    request: error.request,
    response: error.response
      ? {
          status: error.response.status,
          data: error.response.data,
        }
      : undefined,
  };
};

const addAxiosContextRecursive = (
  event: Sentry.Event,
  error: unknown,
): void => {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
};

const handleSentryBeforeSend = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent => {
  addAxiosContextRecursive(event, hint?.originalException);

  return event;
};

export const initSentry = (): void => {
  const appSettings = getAppSettings();
  const sentrySettings = appSettings.analytics.sentry;
  if (!sentrySettings?.enabled) {
    return;
  }

  Sentry.init({
    dsn: sentrySettings.dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.feedbackIntegration({
        autoInject: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/app-review\.crowdaa\.com/,
      /^https:\/\/app\.crowdaa\.com/,
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    release: __COMMIT_SHA__,
    environment: appSettings.environment.name,
    enabled: sentrySettings.enabled,
    beforeSend: handleSentryBeforeSend,
  });
};
